import { AxiosInstance } from 'axios';

export const baseEndpoint = 'blog-pipeline';

interface IdeateResponse {
  ideas: string;
}

interface OutlineResponse {
  outline: string;
}

interface ImageResponse {
  imageUrl: string;
}

interface MultipleImagesResponse {
  results: Array<{
    prompt: string;
    url: string;
    error?: string;
  }>;
}

export type ReviewIssueSeverity = 'error' | 'warning' | 'info';

export interface ReviewIssue {
  severity: ReviewIssueSeverity;
  code: string;
  message: string;
  location?: string;
}

export interface DeterministicReview {
  passed: boolean;
  errorCount: number;
  warningCount: number;
  issues: ReviewIssue[];
}

export interface LlmSuggestion {
  location: string;
  issue: string;
  suggestion: string;
}

export interface LlmReview {
  summary: string;
  suggestions: LlmSuggestion[];
  fixedDraft: Record<string, any> | null;
}

export interface BlogReviewResult {
  deterministic: DeterministicReview;
  llm?: LlmReview;
}

export type SocialPlatform = 'linkedin' | 'instagram' | 'twitter' | 'facebook';

export interface SocialPost {
  platform: SocialPlatform;
  caption: string;
  hashtags: string[];
}

export interface SocialGenerationResponse {
  posts: SocialPost[];
  warnings: string[];
}

export interface SocialEmailResponse {
  emailedTo: string;
  warnings: string[];
}

export class BlogPipelineApi {
  private api: AxiosInstance;

  constructor(api: AxiosInstance) {
    this.api = api;
  }

  /**
   * Step 1: Generate 5 blog topic ideas from a keyword
   */
  async generateTopicIdeas(keyword: string): Promise<string> {
    const response = await this.api.post<IdeateResponse>('/ideate', { keyword });
    return response.data.ideas;
  }

  /**
   * Step 2: Generate a structured outline from a title and keyword
   */
  async generateOutline(title: string, keyword: string): Promise<string> {
    const response = await this.api.post<OutlineResponse>('/outline', { title, keyword });
    return response.data.outline;
  }

  /**
   * Step 3: Generate final JSON payload from outline and keyword
   * This internally generates prose and then the final JSON
   */
  async generateDraft(outline: string, title?: string, keyword?: string): Promise<Record<string, any>> {
    const response = await this.api.post<Record<string, any>>('/draft', { outline, title, keyword });
    return response.data;
  }

  /**
   * Review a generated draft. Deterministic checks always run; pass runLlm=true
   * to additionally run the LLM editorial pass (suggestions + corrected draft).
   */
  async reviewBlog(
    blogJSON: Record<string, any>,
    keyword: string,
    runLlm: boolean
  ): Promise<BlogReviewResult> {
    const response = await this.api.post<BlogReviewResult>('/review', { blogJSON, keyword, runLlm });
    return response.data;
  }

  /**
   * Step 1: generate engagement-optimized social posts for an existing blog
   * post (by slug). Does not send any email — returns the posts for review.
   */
  async generateSocialPosts(
    slug: string,
    platforms?: SocialPlatform[]
  ): Promise<SocialGenerationResponse> {
    const response = await this.api.post<SocialGenerationResponse>('/social', {
      slug,
      platforms,
    });
    return response.data;
  }

  /**
   * Step 2: email the reviewed social posts (with the cover image attached) to
   * the admin. Nothing is persisted.
   */
  async emailSocialPosts(slug: string, posts: SocialPost[]): Promise<SocialEmailResponse> {
    const response = await this.api.post<SocialEmailResponse>('/social/email', {
      slug,
      posts,
    });
    return response.data;
  }

  /**
   * Generate a single image from a text prompt
   */
  async generateImage(imagePrompt: string): Promise<string> {
    const response = await this.api.post<ImageResponse>('/image', { imagePrompt });
    return response.data.imageUrl;
  }

  /**
   * Proxy-download an external image via backend to bypass CSP on direct browser fetch.
   */
  async proxyImage(imageUrl: string): Promise<Blob> {
    const response = await this.api.post('/image-proxy', { imageUrl }, { responseType: 'blob' });
    return response.data as Blob;
  }

  getProxiedImageSrc(imageUrl: string): string {
    const encoded = encodeURIComponent(imageUrl);
    return `/api/${baseEndpoint}/image-proxy?imageUrl=${encoded}`;
  }

  /**
   * Generate multiple images from an array of prompts
   */
  async generateMultipleImages(prompts: string[]): Promise<Array<{ prompt: string; url: string; error?: string }>> {
    const response = await this.api.post<MultipleImagesResponse>('/images', { prompts });
    return response.data.results;
  }
}
